import React, { useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
} from "@xyflow/react";
import Header from "./Header.react.js";
import ComponentLibrary from "./ComponentLibrary.react.js";
import FlowCanvas from "./FlowCanvas.react.js";
import PropertyPanel from "./PropertyPanel.react.js";
import { useFlowState } from "../hooks/useFlowState.js";
import { useLazyOptions } from "../hooks/useLazyOptions.js";
import { serializeGraph } from "../utils/serializeGraph.js";
import { asKvRows } from "../utils/kvRows.js";

/**
 * 顶层组件：组合所有子组件。
 * - React 内部 useState 为主（拖拽顺滑）
 * - 选中节点 + Save 事件外露给 Python 回调
 * - isEdit=True：组件库可点击/拖拽加入、属性面板可编辑、FlowCanvas 可拖/连/删
 * - isEdit=False：组件库按钮 disabled、属性面板只读、FlowCanvas 仍可选中/平移/缩放
 * - 任何修改 allNodes/allEdges 的顶层回调在 isEdit=False 时早返回，
 *   确保查看模式下画布数据不会被外部事件污染
 *
 * 外层包 ReactFlowProvider：useReactFlow 必须在 Provider 内部才能用。
 */
function TestFlowEditorInner(props) {
  const {
    id,
    schema,
    initialNodes = [],
    initialEdges = [],
    isEdit,
    setProps,
    phases,
    dynamicOptions,
    optionResponse,
  } = props;

  // phase 列表：默认只有 1 个 "用例" phase
  const phaseList = phases ?? [{ value: "main", label: "用例" }];

  const flow = useFlowState(initialNodes, initialEdges);

  // 引用数据懒加载状态机：选中节点 → 按需发 optionRequest，合并 optionResponse。
  const lazyOptions = useLazyOptions({
    selectedNode: flow.selectedNode,
    schema,
    dynamicOptions,
    optionResponse,
    setProps,
  });

  // info 节点的 name / description 仅存于 nodes 列表中，不再抽取到 FlowGraph 顶层
  // （FlowGraph 顶层保持纯净：schema_version + saved_at + nodes + edges）

  // 上一次 addNode 的时间戳。ReactFlow v12 内部 store 同步时会偶发
  // 触发对刚加节点的 remove change（与 d3-drag 释放竞态）。500ms 内的
  // remove change 视为误发，忽略。
  // 刚添加节点的 id -> 时间戳。用于精确防护「拖拽释放竞态」：
  // ReactFlow v12 偶发对刚加入的节点误发 remove change，导致节点一加就消失。
  // 只忽略「针对这些新 id」在极短时间内的 remove，不影响对其它已有节点的正常删除。
  const recentAddIds = useRef(new Map());
  // FlowCanvas 暴露的 API（getCanvasCenterFlowPos）；点击添加时取当前视口中心
  const canvasApiRef = useRef(null);
  // 记录上一次推给 Python 的 graph JSON，内容不变则不再 setProps，
  // 避免"纯选中/拖动结束"等操作让 saveTrigger 无意义跳变。
  const lastGraphJson = useRef(null);

  const handleCanvasApi = useCallback((api) => {
    canvasApiRef.current = api;
  }, []);

  // 切 phase 时清掉选中
  useEffect(() => {
    flow.setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.activePhase]);

  // 选中态外露：只推 id 字符串（不是整个节点对象）——因为 propType 是 string
  // flow.selectedNode 本身是整个对象（给 PropertyPanel 用），这里取 id
  useEffect(() => {
    setProps({ selectedNode: flow.selectedNode?.id ?? null });
  }, [flow.selectedNode, setProps]);

  // 画布变化时自动同步 graph + saveTrigger 到 Python。
  // 不再需要"点击保存"——外部 html.Button 调 callback 读 State("editor","graph")
  // 就能拿到最新数据，自己决定什么时候存。
  useEffect(() => {
    if (!setProps) return;
    // 序列化逻辑抽到 utils/serializeGraph.js，方便单独 vitest 覆盖
    // （节点投影清洗 + 邻接表预投影在那一层）
    const content = serializeGraph(flow.allNodes, flow.allEdges, schema);
    // 只比对业务内容（不含 saved_at，否则时间戳每次都变导致比对永远不等）。
    const contentJson = JSON.stringify(content);
    if (contentJson === lastGraphJson.current) return;
    lastGraphJson.current = contentJson;
    // 内容确实变化时才打时间戳并推给 Python
    const graphJson = JSON.stringify({
      ...content,
      saved_at: new Date().toISOString(),
    });
    setProps({
      graph: graphJson,
      saveTrigger: Date.now(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.allNodes, flow.allEdges, schema, setProps]);

  // React Flow 事件桥接到 useFlowState
  const RECENT_ADD_GUARD_MS = 500;
  const handleNodesChange = useCallback(
    (changes) => {
      // 查看模式：禁止任何写操作
      if (!isEdit) return;
      const now = Date.now();
      const safe = changes.filter((c) => {
        // 1. dimensions：ReactFlow 内部测量用，不应写到 state（避免渲染风暴）
        if (c.type === "dimensions") return false;
        // 2. 竞态防护：只忽略「刚添加的那个节点 id」在极短时间内的误发 remove。
        //    对其它已有节点的删除一律放行（修复原先「加节点后 500ms 内任何删除都被吞掉」的误伤）。
        if (c.type === "remove") {
          const addedTs = recentAddIds.current.get(c.id);
          if (addedTs && now - addedTs < RECENT_ADD_GUARD_MS) return false;
        }
        return true;
      });
      // 清理过期的新增 id 记录，避免 Map 无限增长
      for (const [id, ts] of recentAddIds.current) {
        if (now - ts >= RECENT_ADD_GUARD_MS) recentAddIds.current.delete(id);
      }
      if (safe.length === 0) return;
      flow.setAllNodes((nodes) => applyNodeChanges(safe, nodes));
    },
    [flow.setAllNodes, isEdit]
  );
  const handleEdgesChange = useCallback(
    (changes) => {
      // 查看模式：禁止任何写操作
      if (!isEdit) return;
      flow.setAllEdges((edges) => applyEdgeChanges(changes, edges));
    },
    [flow.setAllEdges, isEdit]
  );
  const handleConnect = useCallback(
    (c) => {
      // 查看模式：禁止任何写操作
      if (!isEdit) return;
      if (!c.source || !c.target) return;
      // 只存业务字段 animated；连线样式统一由 FlowCanvas 的 rfEdges 按 selected/animated 计算
      flow.setAllEdges((edges) => addEdge({ ...c, animated: true }, edges));
    },
    [flow.setAllEdges, isEdit]
  );

  // 添加节点：actionType + position
  //   - 拖拽时由 FlowCanvas 算出 position 传进来
  //   - 点击 ComponentLibrary 时 position=null → 取画布视口中心；取不到再走 addNode 固定偏移
  const handleAddNode = (actionType, position = null) => {
    // 查看模式：禁止任何写操作
    if (!isEdit) return;
    const actionSchema = schema?.actions?.[actionType];
    if (!actionSchema) {
      // 真实错误：用户传的 action key 不在 schema 里（保留警告）
      console.warn(
        "[TFE] unknown action",
        actionType,
        "available=",
        Object.keys(schema?.actions ?? {})
      );
      return;
    }
    let pos = position;
    if (pos == null) {
      pos = canvasApiRef.current?.getCanvasCenterFlowPos?.() ?? null;
    }
    // 收集 single_input / single_select 节点的 valueType + 标记位，从 schema.components[0].valueType 读
    const firstComp = actionSchema.components?.[0];
    const isScalar = firstComp && ["single_input", "single_select"].includes(firstComp.type);
    const extras = isScalar
      ? { isSingleInput: true, valueType: firstComp.valueType || "str" }
      : null;
    const newId = flow.addNode(actionType, actionSchema.defaults, pos, extras);
    // 记录刚添加的节点 id + 时间戳，用于精确防护竞态误发的 remove
    if (newId) recentAddIds.current.set(newId, Date.now());
  };

  const handleComponentChange = (nodeId, component, idx, field, value) => {
    // 查看模式：禁止任何写操作
    if (!isEdit) return;
    flow.setAllNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        const rows = [...asKvRows(n.componentData?.[component])];
        rows[idx] = { ...rows[idx], [field]: value };
        return {
          ...n,
          componentData: { ...(n.componentData ?? {}), [component]: rows },
        };
      })
    );
  };

  // 通用 kv_input component: 单字段值变更（无 idx/row 概念）
  // 写入 componentData[component][fieldKey] = value
  const handleComponentValueChange = (nodeId, component, fieldKey, value) => {
    // 查看模式：禁止任何写操作
    if (!isEdit) return;
    flow.setAllNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        const current = n.componentData?.[component] ?? {};
        return {
          ...n,
          componentData: {
            ...(n.componentData ?? {}),
            [component]: { ...current, [fieldKey]: value },
          },
        };
      })
    );
  };

  // 整段 component 替换（用于 api_body 这种多模式 component）
  // 写入 componentData[component] = value（整体替换）
  const handleComponentObjectChange = (nodeId, component, value) => {
    // 查看模式：禁止任何写操作
    if (!isEdit) return;
    flow.setAllNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        return {
          ...n,
          componentData: {
            ...(n.componentData ?? {}),
            [component]: value,
          },
        };
      })
    );
  };

  // single_input：整体替换 componentData 与可选 valueType，原子化写入
  // valueType 可省略：仅传 value 时保留节点原 valueType 不动
  const handleComponentScalarChange = (nodeId, value, valueType) => {
    if (!isEdit) return;
    flow.setAllNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        const next = { ...n, componentData: value };
        if (valueType !== undefined) next.valueType = valueType;
        return next;
      })
    );
  };

  const handleComponentAdd = (nodeId, component, defaultType) => {
    // 查看模式：禁止任何写操作
    if (!isEdit) return;
    flow.setAllNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        const current = asKvRows(n.componentData?.[component]);
        return {
          ...n,
          componentData: {
            ...(n.componentData ?? {}),
            [component]: [...current, { key: "", type: defaultType, val: "" }],
          },
        };
      })
    );
  };

  const handleComponentDelete = (nodeId, component, idx) => {
    // 查看模式：禁止任何写操作
    if (!isEdit) return;
    flow.setAllNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        const rows = [...asKvRows(n.componentData?.[component])];
        rows.splice(idx, 1);
        return {
          ...n,
          componentData: { ...(n.componentData ?? {}), [component]: rows },
        };
      })
    );
  };

  // 节点删除回调：用于 PropertyPanel 的「删除节点」按钮
  // 查看模式：禁止任何写操作
  const handleDeleteNode = useCallback(
    (nodeId) => {
      if (!isEdit) return;
      flow.deleteNode(nodeId);
    },
    [flow.deleteNode, isEdit]
  );

  return (
    <div className="tfe-root" id={id}>
      <Header
        activePhase={flow.activePhase}
        onPhaseChange={(p) => flow.setActivePhase(p)}
        phases={phaseList}
      />
      <div className="tfe-layout">
        <aside className="tfe-sidebar">
          {/* 组件库: 包含 info + load_data + api_request + case_call + assert
              （info 是用例元信息节点，可拖拽 / 点击加入画布）。
              查看模式下仍渲染列表，按钮交给 ComponentLibrary 内部 disable（isEdit=false）。 */}
          <ComponentLibrary
            schema={schema}
            isEdit={isEdit}
            onAdd={handleAddNode}
          />
        </aside>
        <FlowCanvas
          nodes={flow.visibleNodes}
          edges={flow.visibleEdges}
          schema={schema}
          isEdit={isEdit}
          activePhase={flow.activePhase}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          onNodeClick={(nid) => flow.setSelected(nid)}
          onDropAction={handleAddNode}
          onCanvasApi={handleCanvasApi}
          fitOnMount={(initialNodes?.length ?? 0) > 0}
        />
        {flow.selectedId && (
          <PropertyPanel
            node={flow.selectedNode}
            schema={schema}
            isEdit={isEdit}
            lazyOptions={lazyOptions}
            onComponentChange={handleComponentChange}
            onComponentValueChange={handleComponentValueChange}
            onComponentObjectChange={handleComponentObjectChange}
            onComponentScalarChange={handleComponentScalarChange}
            onComponentAdd={handleComponentAdd}
            onComponentDelete={handleComponentDelete}
            onDeleteNode={handleDeleteNode}
            onClose={() => flow.setSelected(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * 外层包装：ReactFlowProvider 是 useReactFlow() 所必需。
 * 默认导出仍为 TestFlowEditor（保持原 API）。
 */
function TestFlowEditor(props) {
  return (
    <ReactFlowProvider>
      <TestFlowEditorInner {...props} />
    </ReactFlowProvider>
  );
}

TestFlowEditor.propTypes = {
  id: PropTypes.string,
  schema: PropTypes.object.isRequired,
  initialNodes: PropTypes.array,
  initialEdges: PropTypes.array,
  isEdit: PropTypes.bool,
  /**
   * 顶部 phase 切换器要显示的列表。每个 {value, label} 渲染一个 tab。
   * 默认 [{value: "main", label: "用例"}]——只一个 phase。
   * 想要 3 phase（setup / main / teardown）就传：
   *   phases=[
   *     {value: "setup", label: "前置"},
   *     {value: "main", label: "核心"},
   *     {value: "teardown", label: "后置"},
   *   ]
   */
  phases: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  setProps: PropTypes.func.isRequired,

  // ↓↓↓ OUTPUT-only props ↓↓↓
  // 这 3 个 prop 由 React 组件内部通过 setProps(...) 推回 Python 端，
  // 用于触发 callback。**不要**在 Python 端 <TestFlowEditor ...> 调用时传入。
  // 标在 propTypes 里只是为了 react-docgen 自动发现，让生成的 Python wrapper
  // 把它们列进 _prop_names / available_properties，否则 callback Input/Output
  // 会报 "does not support this property"。
  // 描述里的 "Read-only OUTPUT:" 前缀会原样编译到 Python __doc__，PyCharm/VSCode
  // 等 IDE 会显示为只读提示。
  /** Read-only OUTPUT: 当前选中节点的 id（null 表示未选中）。由组件 setProps 推回，不要从 Python 传入。 */
  selectedNode: PropTypes.string,
  /** Read-only OUTPUT: 当前画布的 FlowGraph JSON 字符串。画布变化时（添加/删除/拖动节点、连线等）由组件自动 setProps 推回，不要从 Python 传入。 */
  graph: PropTypes.string,
  /** Read-only OUTPUT: 画布变化时间戳（毫秒）。每次画布变化（添加/删除/拖动节点、连线等）自动更新。监听它的 Input 会在画布变动时触发 callback——**不是**"用户点保存"的信号。由组件 setProps 推回，不要从 Python 传入。 */
  saveTrigger: PropTypes.number,

  // ↓↓↓ 动态下拉（source）运行时接口 ↓↓↓

  /** 外部预置的动态下拉选项，以 action key 寻址：{ dataset: [{value, name}, ...] }。可选；优先级低于组件内部 runtimeOptions，高于 Schema 静态 options。 */
  dynamicOptions: PropTypes.object,

  /** Read-only OUTPUT: 懒加载请求事件。选中带 source 的 single_select 节点时由组件 setProps 推回：{requestId, action, sources, values, triggeredAt}。不要从 Python 传入。 */
  optionRequest: PropTypes.object,

  /** 底层 Dash callback 写回的懒加载响应：{requestId, action, status: "success"|"error", options, message}。瞬时事件，不写入 FlowGraph。 */
  optionResponse: PropTypes.object,
};

export default TestFlowEditor;
